import React from 'react'
import Router from 'next/router'
import mitt from 'next/dist/next-server/lib/mitt'

const XHR_COUNTING_NOTIFY_EVENT_NAME =
  '__AL_EX_XHR_COUNTING_NOTIFY_qXdTr8HCwJUQXCGJ'

function computePerformanceLoadReady(callback) {
  // xhr 请求数为 0，且 rAF 后请求数仍为 0，视为 ready
  if (process.browser) {
    const taskInfo = window['__AL_EX_XHR_COUNTING_qXdTr8HCwJUQXCGJ']
    let isRemoveListener = false
    const listener = () => {
      if (taskInfo.count === 0) {
        requestAnimationFrame(() => {
          if (isRemoveListener) {
            return
          }
          // 再次判断，两个连续帧都没有 xhr
          if (taskInfo.count === 0) {
            isRemoveListener = true
            document.removeEventListener(
              XHR_COUNTING_NOTIFY_EVENT_NAME,
              listener
            )
            callback()
          }
        })
      }
    }
    document.addEventListener(XHR_COUNTING_NOTIFY_EVENT_NAME, listener, false)
    listener() // 立即触发一次
  }
}

export function withPerformanceApp(App, internalScope) {
  return process.server
    ? App
    : class extends React.PureComponent {
        static displayName = `fwAppWithPerformance(${
          App.displayName || App.name || 'App'
        })`

        static async getInitialProps(appCtx) {
          let initialProps = {}
          if ('getInitialProps' in App) {
            initialProps = await App.getInitialProps(appCtx)
          }
          return initialProps
        }

        constructor(props) {
          super(props)

          // Router events，对于首个页面访问不会触发（除非业务手动跳转）
          // 而首个页面的时间同样需要监控
          // 利用 eventemmiter 整合 routerEvents 和 constructor/didMount
          // 不引用新库，直接用 Router 的事件库
          this.mitt = mitt()
          this.bindRouterEvents()

          this.isCurrentRouterChanging = false
          this.latestRouteIndex = 0
          this.handleRouteChangeStart = this.handleRouteChangeStart.bind(this)
          this.mitt.on('routeChangeStart', this.handleRouteChangeStart)

          // 首个页面时触发，其余 csr routeChange 由 router emit
          this.mitt.emit('routeChangeStart')
        }

        componentDidMount() {
          window.AL_REPORT_SPEED_POINT('APP_ACTION', Date.now())
          // APP_READY 基于 ROUTE_READY 推导，在 global.js 上处理

          // 模拟首页的 complete，其余 csr routeChange 由 router emit
          this.mitt.emit('routeChangeComplete')
        }

        componentWillUnmount() {
          this.unbindRouterEvents()
        }

        bindRouterEvents() {
          ;[
            'routeChangeStart',
            'routeChangeComplete',
            'routeChangeError',
          ].forEach((eventName) => {
            this[`routerEvents_${eventName}`] = () => {
              this.mitt.emit(eventName)
            }
            Router.events.on(eventName, this[`routerEvents_${eventName}`])
          })
        }

        unbindRouterEvents() {
          ;[
            'routeChangeStart',
            'routeChangeComplete',
            'routeChangeError',
          ].forEach((eventName) => {
            Router.events.off(eventName, this[`routerEvents_${eventName}`])
          })
        }

        handleRouteChangeStart() {
          if (!this.isCurrentRouterChanging) {
            // 新的路由变化
            this.isCurrentRouterChanging = true
          } else {
            // 已有路由变化进行中，说明重复的发生了路由变化
            // ROUTE_LEAVE 时间不变，ROUTE_ACTION、ROUTE_READY 顺延为新页面
          }
          this.latestRouteIndex++
          // 利用函数闭包，保留触发执行时的 latestRouteIndex
          // 函数内检查参数是否与this一致，判断是否是最近的一次统计。使得可以正确监控连续跳转的场景
          this.mitt.on(
            'routeChangeComplete',
            this.createHandleRouteChangeComplete(
              this.latestRouteIndex,
              Date.now()
            )
          )
          this.mitt.on(
            'routeChangeError',
            this.createHandleRouteChangeError(this.latestRouteIndex)
          )
        }

        createHandleRouteChangeComplete(createRouteIndex, routeLeaveTime) {
          const handler = function () {
            this.mitt.off('routeChangeComplete', handler)
            if (this.latestRouteIndex !== createRouteIndex) {
              return
            }
            // 新页面已经 didMount，但请求可能还在进行中
            const routeActionTime = Date.now()
            // 等待请求完成，通过 ajax 全局监听请求
            computePerformanceLoadReady(() => {
              if (this.latestRouteIndex !== createRouteIndex) {
                return
              }
              const routeReadyTime = Date.now()
              window.AL_REPORT_SPEED_POINT('ROUTE_CHANGE_ARRAY', [
                routeLeaveTime,
                routeActionTime,
                routeReadyTime,
              ])
              this.isCurrentRouterChanging = false
            })
          }.bind(this)
          return handler
        }

        createHandleRouteChangeError(createRouteIndex) {
          const handler = function () {
            this.mitt.off('routeChangeError', handler)
            if (this.latestRouteIndex !== createRouteIndex) {
              return
            }
            // 路由已经发生错误，停留在当前页，视为未发生过路由变化
            this.isCurrentRouterChanging = false
          }.bind(this)
          return handler
        }

        render() {
          return <App {...this.props} />
        }
      }
}
